import { semanticColors, type ThemeName } from './semantic';
import { themeTokens } from './tokens';

// Component colors map semantic colors to specific UI components
export const componentColors = {
  light: {
    // Toggle Colors
    toggleEnabled: themeTokens.colors.light.toggleEnabled,
    toggleDisabled: themeTokens.colors.light.toggleDisabled,
    toggleBorder: themeTokens.colors.light.toggleBorder,

    // App Layout
    app: {
      background: semanticColors.light.background,
      backgroundTransparent: semanticColors.light.transparentBackground,
      backgroundTransparentHover: semanticColors.light.transparentBackgroundHover,
      text: semanticColors.light.textPrimary,
    },

    // Settings
    settings: {
      background: semanticColors.light.background,
      surfacePrimary: semanticColors.light.surface,
      surfaceSecondary: semanticColors.light.surfaceHover,
      inputBackground: semanticColors.light.surface,
      inputBorder: semanticColors.light.border,
      heading: semanticColors.light.textHeading,
      text: semanticColors.light.textBody,
      description: semanticColors.light.textDescription,
      inputText: semanticColors.light.textBody,
      error: semanticColors.light.textError,
      tabBackground: semanticColors.light.surfaceHover,
      tabHover: semanticColors.light.surface,
      tabActive: semanticColors.light.accent,
      tabBorder: semanticColors.light.border,
    },

    // Forms
    form: {
      background: semanticColors.light.background,
      modalOverlay: 'rgba(0, 0, 0, 0.5)',
      cardBackground: semanticColors.light.surface,
      inputBackground: semanticColors.light.surface,
      inputBorder: semanticColors.light.border,
      inputText: semanticColors.light.textPrimary,
      buttonPrimary: semanticColors.light.accent,
      buttonSecondary: semanticColors.light.surfaceHover,
    },

    // Sidebar
    sidebar: {
      background: themeTokens.colors.light.sidebarBackground,
      border: semanticColors.light.borderTransparent,
      text: themeTokens.colors.light.sidebarText,
      textSecondary: themeTokens.colors.light.sidebarTextHover,
      textSelected: themeTokens.colors.light.sidebarTextSelected,
      hoverBackground: themeTokens.colors.light.sidebarBackgroundHover,
      activeBackground: semanticColors.light.surfaceTransparent,
      icon: semanticColors.light.actionDefault,
      iconHover: semanticColors.light.actionHover,
      folderIcon: semanticColors.light.folderIcon,
      collapseButton: themeTokens.colors.light.sidebarCollapseButton,
      collapseButtonHover: themeTokens.colors.light.sidebarCollapseButtonHover,
      collapseLabel: themeTokens.colors.light.sidebarCollapseLabel,
      collapseLabelHover: themeTokens.colors.light.sidebarCollapseLabelHover,
    },

    // Prompt Card
    promptCard: {
      background: semanticColors.light.cardBackground,
      backgroundHover: semanticColors.light.cardBackgroundHover,
      border: semanticColors.light.borderTransparent,
      title: themeTokens.colors.light.textHeading,
      description: themeTokens.colors.light.textDescription,
      text: themeTokens.colors.light.textBody,
      codeBackground: semanticColors.light.codeBackground,
      actionIcon: semanticColors.light.actionDefault,
      actionIconHover: semanticColors.light.actionHover,
    },

    // Action Menu
    actionMenu: {
      background: semanticColors.light.menuBackground,
      backgroundHover: semanticColors.light.menuHover,
      icon: semanticColors.light.textSecondary,
      iconHover: semanticColors.light.textPrimary,
      useAction: semanticColors.light.actionMenu.useAction,
      openAction: semanticColors.light.actionMenu.openAction,
      editAction: semanticColors.light.actionMenu.editAction,
      deleteAction: semanticColors.light.actionMenu.deleteAction,
    },

    // App Header
    header: {
      appName: semanticColors.light.textBrand,
      appNameHover: semanticColors.light.textBrandHover,
    },

    // Buttons
    button: {
      primary: {
        background: semanticColors.light.buttonPrimary,
        backgroundHover: semanticColors.light.buttonPrimaryHover,
        text: semanticColors.light.buttonPrimaryText,
      },
      secondary: {
        background: semanticColors.light.buttonSecondary,
        backgroundHover: semanticColors.light.buttonSecondaryHover,
        text: semanticColors.light.buttonSecondaryText,
      },
      disabled: {
        background: semanticColors.light.buttonDisabled,
        text: semanticColors.light.textDisabled,
      },
    },
  },

  dark: {
    // Toggle Colors
    toggleEnabled: themeTokens.colors.dark.toggleEnabled,
    toggleDisabled: themeTokens.colors.dark.toggleDisabled,
    toggleBorder: themeTokens.colors.dark.toggleBorder,

    // App Layout
    app: {
      background: semanticColors.dark.background,
      backgroundTransparent: semanticColors.dark.transparentBackground,
      backgroundTransparentHover: semanticColors.dark.transparentBackgroundHover,
      text: semanticColors.dark.textPrimary,
    },

    // Settings
    settings: {
      background: semanticColors.dark.background,
      surfacePrimary: semanticColors.dark.surface,
      surfaceSecondary: semanticColors.dark.surfaceHover,
      inputBackground: semanticColors.dark.surface,
      inputBorder: semanticColors.dark.border,
      heading: semanticColors.dark.textHeading,
      text: semanticColors.dark.textBody,
      description: semanticColors.dark.textDescription,
      inputText: semanticColors.dark.textBody,
      error: semanticColors.dark.textError,
      tabBackground: semanticColors.dark.surfaceHover,
      tabHover: semanticColors.dark.surface,
      tabActive: semanticColors.dark.accent,
      tabBorder: semanticColors.dark.border,
    },

    // Forms
    form: {
      background: semanticColors.dark.background,
      modalOverlay: 'rgba(0, 0, 0, 0.8)',
      cardBackground: semanticColors.dark.surface,
      inputBackground: semanticColors.dark.surface,
      inputBorder: semanticColors.dark.border,
      inputText: semanticColors.dark.textPrimary,
      buttonPrimary: semanticColors.dark.accent,
      buttonSecondary: semanticColors.dark.surfaceHover,
    },

    // Sidebar
    sidebar: {
      background: themeTokens.colors.dark.sidebarBackground,
      border: semanticColors.dark.borderTransparent,
      text: themeTokens.colors.dark.sidebarText,
      textSecondary: themeTokens.colors.dark.sidebarTextHover,
      textSelected: themeTokens.colors.dark.sidebarTextSelected,
      hoverBackground: themeTokens.colors.dark.sidebarBackgroundHover,
      activeBackground: semanticColors.dark.surfaceTransparent,
      icon: semanticColors.dark.actionDefault,
      iconHover: semanticColors.dark.actionHover,
      folderIcon: semanticColors.dark.folderIcon,
      collapseButton: themeTokens.colors.dark.sidebarCollapseButton,
      collapseButtonHover: themeTokens.colors.dark.sidebarCollapseButtonHover,
      collapseLabel: themeTokens.colors.dark.sidebarCollapseLabel,
      collapseLabelHover: themeTokens.colors.dark.sidebarCollapseLabelHover,
    },

    // Prompt Card
    promptCard: {
      background: semanticColors.dark.cardBackground,
      backgroundHover: semanticColors.dark.cardBackgroundHover,
      border: semanticColors.dark.borderTransparent,
      title: themeTokens.colors.dark.textHeading,
      description: themeTokens.colors.dark.textDescription,
      text: themeTokens.colors.dark.textBody,
      codeBackground: semanticColors.dark.codeBackground,
      actionIcon: semanticColors.dark.actionDefault,
      actionIconHover: semanticColors.dark.actionHover,
    },

    // Action Menu
    actionMenu: {
      background: semanticColors.dark.menuBackground,
      backgroundHover: semanticColors.dark.menuHover,
      icon: semanticColors.dark.textSecondary,
      iconHover: semanticColors.dark.textPrimary,
      useAction: semanticColors.dark.actionMenu.useAction,
      openAction: semanticColors.dark.actionMenu.openAction,
      editAction: semanticColors.dark.actionMenu.editAction,
      deleteAction: semanticColors.dark.actionMenu.deleteAction,
    },

    // App Header
    header: {
      appName: semanticColors.dark.textBrand,
      appNameHover: semanticColors.dark.textBrandHover,
    },

    // Buttons
    button: {
      primary: {
        background: semanticColors.dark.buttonPrimary,
        backgroundHover: semanticColors.dark.buttonPrimaryHover,
        text: semanticColors.dark.buttonPrimaryText,
      },
      secondary: {
        background: semanticColors.dark.buttonSecondary,
        backgroundHover: semanticColors.dark.buttonSecondaryHover,
        text: semanticColors.dark.buttonSecondaryText,
      },
      disabled: {
        background: semanticColors.dark.buttonDisabled,
        text: semanticColors.dark.textDisabled,
      },
    },
  },

  spotify: {
    // Toggle Colors
    toggleEnabled: themeTokens.colors.spotify.toggleEnabled,
    toggleDisabled: themeTokens.colors.spotify.toggleDisabled,
    toggleBorder: themeTokens.colors.spotify.toggleBorder,

    // App Layout
    app: {
      background: semanticColors.spotify.background,
      backgroundTransparent: semanticColors.spotify.transparentBackground,
      backgroundTransparentHover: semanticColors.spotify.transparentBackgroundHover,
      text: semanticColors.spotify.textPrimary,
    },

    // Settings
    settings: {
      background: semanticColors.spotify.background,
      surfacePrimary: semanticColors.spotify.surface,
      surfaceSecondary: semanticColors.spotify.surfaceHover,
      inputBackground: semanticColors.spotify.surface,
      inputBorder: semanticColors.spotify.border,
      heading: semanticColors.spotify.textHeading,
      text: semanticColors.spotify.textBody,
      description: semanticColors.spotify.textDescription,
      inputText: semanticColors.spotify.textBody,
      error: semanticColors.spotify.textError,
      tabBackground: semanticColors.spotify.surfaceHover,
      tabHover: semanticColors.spotify.surface,
      tabActive: semanticColors.spotify.accent,
      tabBorder: semanticColors.spotify.border,
    },

    // Forms
    form: {
      background: semanticColors.spotify.form.background,
      modalOverlay: semanticColors.spotify.form.modalOverlay,
      cardBackground: semanticColors.spotify.form.cardBackground,
      inputBackground: semanticColors.spotify.form.inputBackground,
      inputBorder: semanticColors.spotify.form.inputBorder,
      inputText: semanticColors.spotify.form.inputText,
      buttonPrimary: semanticColors.spotify.form.buttonPrimary,
      buttonSecondary: semanticColors.spotify.form.buttonSecondary,
    },

    // Sidebar
    sidebar: {
      background: themeTokens.colors.spotify.sidebarBackground,
      border: semanticColors.spotify.borderTransparent,
      text: themeTokens.colors.spotify.sidebarText,
      textSecondary: themeTokens.colors.spotify.sidebarTextHover,
      textSelected: themeTokens.colors.spotify.sidebarTextSelected,
      hoverBackground: themeTokens.colors.spotify.sidebarBackgroundHover,
      activeBackground: semanticColors.spotify.surfaceTransparent,
      icon: semanticColors.spotify.actionDefault,
      iconHover: semanticColors.spotify.actionHover,
      folderIcon: semanticColors.spotify.folderIcon,
      collapseButton: themeTokens.colors.spotify.sidebarCollapseButton,
      collapseButtonHover: themeTokens.colors.spotify.sidebarCollapseButtonHover,
      collapseLabel: themeTokens.colors.spotify.sidebarCollapseLabel,
      collapseLabelHover: themeTokens.colors.spotify.sidebarCollapseLabelHover,
    },

    // Prompt Card
    promptCard: {
      background: semanticColors.spotify.cardBackground,
      backgroundHover: semanticColors.spotify.cardBackgroundHover,
      border: semanticColors.spotify.borderTransparent,
      title: themeTokens.colors.spotify.textHeading,
      description: themeTokens.colors.spotify.textDescription,
      text: themeTokens.colors.spotify.textBody,
      codeBackground: semanticColors.spotify.codeBackground,
      actionIcon: semanticColors.spotify.actionDefault,
      actionIconHover: semanticColors.spotify.actionHover,
    },

    // Action Menu
    actionMenu: {
      background: semanticColors.spotify.menuBackground,
      backgroundHover: semanticColors.spotify.menuHover,
      icon: semanticColors.spotify.textSecondary,
      iconHover: semanticColors.spotify.textPrimary,
      useAction: semanticColors.spotify.actionMenu.useAction,
      openAction: semanticColors.spotify.actionMenu.openAction,
      editAction: semanticColors.spotify.actionMenu.editAction,
      deleteAction: semanticColors.spotify.actionMenu.deleteAction,
    },

    // App Header
    header: {
      appName: semanticColors.spotify.textBrand,
      appNameHover: semanticColors.spotify.textBrandHover,
    },

    // Buttons
    button: {
      primary: {
        background: semanticColors.spotify.buttonPrimary,
        backgroundHover: semanticColors.spotify.buttonPrimaryHover,
        text: semanticColors.spotify.buttonPrimaryText,
      },
      secondary: {
        background: semanticColors.spotify.buttonSecondary,
        backgroundHover: semanticColors.spotify.buttonSecondaryHover,
        text: semanticColors.spotify.buttonSecondaryText,
      },
      disabled: {
        background: semanticColors.spotify.buttonDisabled,
        text: semanticColors.spotify.textDisabled,
      },
    },
  },
} as const;

export type ComponentColors = typeof componentColors;
export type ComponentColorName = keyof ComponentColors[ThemeName];
